// [XLSX-WRITER 2026-08-18] A tiny, dependency-free .xlsx writer — enough of the
// OOXML spreadsheet format to emit one sheet with typed cells.
//
// WHY THIS EXISTS. The Inventory export used to be a CSV. A CSV carries no cell
// types, so Excel re-guesses the type of every cell on open, and it gets two of our
// columns wrong:
//   • Company Record ID — an 11-digit HubSpot id — is read as a NUMBER and displayed
//     as 5.75279E+10. Save the file and Excel writes that display text back, so the
//     id is destroyed. (The old CSV carried a comment claiming the id "stays TEXT on
//     purpose"; it did not. Skipping a Number() call in JS changes nothing about the
//     characters written to the file, and a CSV has no way to say "this is text".)
//   • Month ("Jun 2026") was date-coerced the same way — worked around previously by
//     hiding a U+00A0 in the value, the one separator Excel's date parser rejects.
// A real .xlsx states each cell's type explicitly, so Excel never guesses. That fixes
// both columns properly and lets the U+00A0 workaround go.
//
// Deliberately NOT a dependency (no SheetJS/exceljs): we need one sheet of strings
// and numbers, which is a few hundred bytes of XML in a ZIP. Everything below is the
// minimum the format requires — the parts are STOREd (no compression), so there is no
// deflate implementation here either.

/** A cell: a number is written as numeric, everything else as an inline string. */
export type XlsxCell = string | number;

/* ── ZIP plumbing ─────────────────────────────────────────────────────────────── */

const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let c = i;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[i] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

interface ZipEntry { name: string; data: Uint8Array; crc: number; offset: number }

/** Build a ZIP archive with every part STOREd. Enough for .xlsx — Excel does not
 *  require the parts to be deflated. Fixed 1980-01-01 timestamps keep the output
 *  byte-identical for identical input.
 *  [EOM-XLSX 2026-10-08] Exported (with esc/colName) for the EOM Dashboard workbook — nothing changed. */
export function zip(files: { name: string; text: string }[]): Uint8Array<ArrayBuffer> {
  const enc = new TextEncoder();
  const entries: ZipEntry[] = [];
  const chunks: Uint8Array[] = [];
  let offset = 0;

  const push = (b: Uint8Array) => { chunks.push(b); offset += b.length; };

  for (const f of files) {
    const data = enc.encode(f.text);
    const name = enc.encode(f.name);
    const crc = crc32(data);
    const head = new Uint8Array(30 + name.length);
    const dv = new DataView(head.buffer);
    dv.setUint32(0, 0x04034b50, true);   // local file header signature
    dv.setUint16(4, 20, true);           // version needed
    dv.setUint16(6, 0x0800, true);       // flags: UTF-8 names
    dv.setUint16(8, 0, true);            // method: store
    dv.setUint16(10, 0, true);           // mod time
    dv.setUint16(12, 33, true);          // mod date = 1980-01-01
    dv.setUint32(14, crc, true);
    dv.setUint32(18, data.length, true); // compressed size
    dv.setUint32(22, data.length, true); // uncompressed size
    dv.setUint16(26, name.length, true);
    dv.setUint16(28, 0, true);           // extra length
    head.set(name, 30);
    entries.push({ name: f.name, data, crc, offset });
    push(head);
    push(data);
  }

  const cdStart = offset;
  for (const e of entries) {
    const name = enc.encode(e.name);
    const rec = new Uint8Array(46 + name.length);
    const dv = new DataView(rec.buffer);
    dv.setUint32(0, 0x02014b50, true);   // central directory signature
    dv.setUint16(4, 20, true);           // version made by
    dv.setUint16(6, 20, true);           // version needed
    dv.setUint16(8, 0x0800, true);       // flags: UTF-8 names
    dv.setUint16(10, 0, true);           // method: store
    dv.setUint16(12, 0, true);           // mod time
    dv.setUint16(14, 33, true);          // mod date
    dv.setUint32(16, e.crc, true);
    dv.setUint32(20, e.data.length, true);
    dv.setUint32(24, e.data.length, true);
    dv.setUint16(28, name.length, true);
    dv.setUint16(38, 0, true);           // internal attrs
    dv.setUint32(42, e.offset, true);    // local header offset
    rec.set(name, 46);
    push(rec);
  }

  const end = new Uint8Array(22);
  const dv = new DataView(end.buffer);
  dv.setUint32(0, 0x06054b50, true);     // end-of-central-directory signature
  dv.setUint16(8, entries.length, true);
  dv.setUint16(10, entries.length, true);
  dv.setUint32(12, offset - cdStart, true);
  dv.setUint32(16, cdStart, true);
  push(end);

  const out = new Uint8Array(new ArrayBuffer(offset));
  let at = 0;
  for (const c of chunks) { out.set(c, at); at += c.length; }
  return out;
}

/* ── Spreadsheet parts ────────────────────────────────────────────────────────── */

export function esc(s: string): string {
  let out = '';
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    // XML 1.0 forbids control characters other than tab, LF and CR. A stray one
    // anywhere in the sheet makes Excel refuse to open the whole workbook, and
    // websites/emails here are untrusted free text, so drop them.
    if (code < 32 && code !== 9 && code !== 10 && code !== 13) continue;
    const ch = s[i];
    if (ch === '&') out += '&amp;';
    else if (ch === '<') out += '&lt;';
    else if (ch === '>') out += '&gt;';
    else out += ch;
  }
  return out;
}

/** 0 → A, 25 → Z, 26 → AA … (spreadsheet column letters are base-26 bijective). */
export function colName(i: number): string {
  let s = '';
  for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) {
    s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
  }
  return s;
}

function cellXml(ref: string, v: XlsxCell, styleId: number): string {
  const s = styleId ? ` s="${styleId}"` : '';
  if (typeof v === 'number' && Number.isFinite(v)) return `<c r="${ref}"${s}><v>${v}</v></c>`;
  const text = v == null ? '' : String(v);
  if (text === '') return `<c r="${ref}"${s}/>`;
  // t="inlineStr" is the whole point: it tells Excel this cell IS text, so it never
  // re-guesses the type — no scientific notation, no date coercion, ever.
  return `<c r="${ref}"${s} t="inlineStr"><is><t xml:space="preserve">${esc(text)}</t></is></c>`;
}

/**
 * Serialise one worksheet to .xlsx bytes.
 *  - `header` is written as row 1 in bold and frozen, so it stays visible when scrolling.
 *  - numbers stay numbers (Excel can sum and pivot them); everything else is text.
 *  - column widths are sized from the content so ids and websites are readable on open.
 */
export function buildXlsx(sheetName: string, header: string[], rows: XlsxCell[][]): Uint8Array<ArrayBuffer> {
  const widths = header.map((h, i) => {
    let max = String(h ?? '').length;
    for (const r of rows) {
      const len = String(r[i] ?? '').length;
      if (len > max) max = len;
    }
    return Math.min(Math.max(max + 2, 9), 60);
  });

  const cols = widths.length
    ? `<cols>${widths.map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`).join('')}</cols>`
    : '';

  // [XLSX-HEAD-ALIGN 2026-09-30, user] "the alignment should be proper for all the data": Excel puts
  // numbers on the right and text on the left, so a plain bold heading sat left of its right-aligned
  // figures. A column whose filled cells are all numbers gets a right-aligned heading (style 2).
  const numericCol = header.map((_, i) => {
    let seen = false;
    for (const r of rows) {
      const v = r[i];
      if (v == null || v === '') continue;
      if (typeof v !== 'number') return false;
      seen = true;
    }
    return seen;
  });
  const headerRow = `<row r="1">${header.map((h, i) => cellXml(`${colName(i)}1`, h, numericCol[i] ? 2 : 1)).join('')}</row>`;
  const bodyRows = rows
    .map((r, ri) => {
      const n = ri + 2;
      const cells = header.map((_, ci) => cellXml(`${colName(ci)}${n}`, r[ci] ?? '', 0)).join('');
      return `<row r="${n}">${cells}</row>`;
    })
    .join('');

  const lastRef = `${colName(Math.max(header.length - 1, 0))}${rows.length + 1}`;
  const sheet =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    `<dimension ref="A1:${lastRef}"/>` +
    `<sheetViews><sheetView workbookViewId="0">` +
    `<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>` +
    `</sheetView></sheetViews>` +
    `<sheetFormatPr defaultRowHeight="15"/>${cols}` +
    `<sheetData>${headerRow}${bodyRows}</sheetData>` +
    `<autoFilter ref="A1:${lastRef}"/>` +
    `</worksheet>`;

  const contentTypes =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
    `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
    `<Default Extension="xml" ContentType="application/xml"/>` +
    `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
    `<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>` +
    `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>` +
    `</Types>`;

  const rels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>` +
    `</Relationships>`;

  const workbook =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ` +
    `xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
    `<sheets><sheet name="${esc(sheetName).slice(0, 31)}" sheetId="1" r:id="rId1"/></sheets>` +
    `</workbook>`;

  const workbookRels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>` +
    `<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
    `</Relationships>`;

  // Three cell formats: 0 = normal, 1 = bold (the header row), 2 = bold + right (a number column's
  // header — [XLSX-HEAD-ALIGN 2026-09-30]). Excel expects the
  // gray125 fill to exist at index 1 even when unused.
  const styles =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    `<fonts count="2">` +
    `<font><sz val="11"/><name val="Calibri"/></font>` +
    `<font><b/><sz val="11"/><name val="Calibri"/></font>` +
    `</fonts>` +
    `<fills count="2"><fill><patternFill patternType="none"/></fill>` +
    `<fill><patternFill patternType="gray125"/></fill></fills>` +
    `<borders count="1"><border/></borders>` +
    `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>` +
    `<cellXfs count="3">` +
    `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>` +
    `<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>` +
    // [XLSX-HEAD-ALIGN 2026-09-30] 2 = bold + right-aligned: the heading of a number column.
    `<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="right"/></xf>` +
    `</cellXfs>` +
    `</styleSheet>`;

  return zip([
    { name: '[Content_Types].xml', text: contentTypes },
    { name: '_rels/.rels', text: rels },
    { name: 'xl/workbook.xml', text: workbook },
    { name: 'xl/_rels/workbook.xml.rels', text: workbookRels },
    { name: 'xl/styles.xml', text: styles },
    { name: 'xl/worksheets/sheet1.xml', text: sheet },
  ]);
}

/** Trigger a browser download of `bytes` as an .xlsx file. */
export function downloadXlsx(filename: string, bytes: Uint8Array<ArrayBuffer>): void {
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
